<template>
    <div class="ai-generation" :class="{ 'initial-state': history.length === 0 && !isLoading }">
        <div v-if="history.length > 0 || isLoading" class="chat-history">
            <div v-for="(message, index) in history" :key="index" :class="['message', message.sender]">
                <p v-html="formatMessage(message.text)"></p>
                <v-tooltip v-if="message.resumeInfo" text="Restore this version" location="top">
                    <template v-slot:activator="{ props }">
                        <v-btn v-bind="props" icon="ph-clock-counter-clockwise" @click="restoreCheckpoint(index)"
                            class="restore-button" variant="text" size="x-small"></v-btn>
                    </template>
                </v-tooltip>
            </div>
            <div v-if="isLoading" class="message assistant">
                <p><i>Generating...</i></p>
            </div>
        </div>
        <div v-if="history.length > 0" class="chat-input-container">
            <div class="chat-input">
                <v-textarea v-model="userInput" @keydown.enter="handleEnter" placeholder="Type your message..." rows="2"
                    auto-grow variant="solo" class="chat-textarea" hide-details rounded>
                    <template v-slot:append-inner>
                        <v-tooltip :text="recordButtonTooltip">
                            <template v-slot:activator="{ props }">
                                <v-btn v-bind="props" :icon="isRecording ? 'ph-stop' : 'ph-microphone'"
                                    @click="toggleRecording" :disabled="isLoading || !openaiApiKey"
                                    :color="isRecording ? 'red' : 'editor-text-muted'" variant="text"
                                    class="record-button" size="small"></v-btn>
                            </template>
                        </v-tooltip>
                        <v-tooltip text="Send Message">
                            <template v-slot:activator="{ props }">
                                <v-btn v-bind="props" icon="ph-paper-plane-right" @click="sendMessage"
                                    :disabled="isLoading || !userInput.trim()" variant="text" class="send-button"
                                    color="editor-text-muted" size="small"></v-btn>
                            </template>
                        </v-tooltip>
                    </template>
                </v-textarea>
            </div>
            <div class="chat-actions">
                <v-tooltip text="Clear History">
                    <template v-slot:activator="{ props }">
                        <v-btn v-bind="props" icon="ph-trash" @click="clearHistory" variant="text" color="error"
                            class="clear-button"></v-btn>
                    </template>
                </v-tooltip>
            </div>
            <div v-if="isTranscribing" class="transcribing-indicator">
                <p><i>Transcribing...</i></p>
            </div>
        </div>
        <div v-if="history.length === 0 && !isLoading" class="initial-actions">
            <button type="button" class="action-card" :disabled="isLoading" @click="editExistingResume">
                <v-icon icon="ph-magic-wand" size="22" />
                <span class="action-text">
                    <strong>Improve my resume</strong>
                    <span>Chat about wording, achievements and what to change.</span>
                </span>
            </button>
            <button type="button" class="action-card" :disabled="isLoading" @click="startFromScratch">
                <v-icon icon="ph-chat-circle-text" size="22" />
                <span class="action-text">
                    <strong>Start from scratch</strong>
                    <span>Answer a few questions and get a first draft. Replaces the current content.</span>
                </span>
            </button>
        </div>
    </div>
</template>

<script>
import AIGenerationService from "../../../services/AIGenerationService";
import TranscriptionService from "../../../services/TranscriptionService";

const PROMPT_EDIT = `
You are an experienced HR and career consultant helping a user improve an existing resume.

Goals:
- Understand what the user wants to improve or change.
- Use the information they provide to suggest edits or updates to their resume.
- Always ask clarifying questions if information is missing or unclear before making changes.

Instructions:
1. Start by asking the user what specific parts of their resume they want to improve (e.g., formatting, phrasing, adding achievements).
2. The resume follows the JSON Resume format: basics (name, title, contacts, summary, profiles), work, education, skills, languages, projects, volunteer, awards, certificates, publications, interests and references.
3. When the user provides information, incorporate it into your suggestions and show an updated draft.
4. Text fields may use Markdown (bold, italic, links); put bullet points in the "highlights" arrays.
5. Stay collaborative: confirm changes with the user before finalizing.
6. Do not show the resume draft or raw text to the user.

Example:
- You: "Which section of your resume would you like to work on first: Work History, Skills, or something else?"
`;

const PROMPT_SCRATCH = `
You are an experienced HR and career consultant guiding a user to build a resume from scratch.

Goals:
- Collect one piece of information at a time.
- Build a complete, well-structured resume step by step.
- Proactively request the next field if you already have the previous one.

Instructions:
1. Ask for the following fields in order: Full Name → Title → Work History → Education → Skills → Hobbies (or other optional sections).
2. Start by asking for the user's full name. Once provided, move on to the next field.
3. Be specific in your requests (e.g., "Could you list your most recent job title and employer?").
4. If any information is unclear or missing, ask for clarification before adding it.
5. As soon as you receive new information, add it to the resume draft (use placeholders if necessary).
6. Put the information in the matching JSON Resume fields; text may use Markdown and bullet points go in the "highlights" arrays.
7. Do not show the resume draft or raw text to the user.

Example:
- You: "Let's build your resume. Could you please provide your full name?"
- User: "John Doe"
- You: "Great! What's your current professional title?"
- User: "Software Engineer at Google"
- You: "Perfect. Could you share your work history (jobs, dates, responsibilities)?"
`;


export default {
    name: "AIGeneration",
    emits: ["update:resumeInfo", "need-settings"],
    props: {
        resumeInfo: {
            type: Object,
            required: true,
        },
        generationApiKey: {
            type: String,
            required: true,
        },
        generationModel: {
            type: String,
            required: true,
        },
        openaiApiKey: {
            type: String,
            required: true,
        },
        transcriptionLanguage: {
            type: String,
            default: null,
        }
    },
    data() {
        return {
            userInput: "",
            history: [],
            isLoading: false,
            activePrompt: null,
            isRecording: false,
            isTranscribing: false,
            mediaRecorder: null,
            audioChunks: [],
        };
    },
    computed: {
        recordButtonTooltip() {
            if (!this.openaiApiKey) {
                return "OpenAI API key is required for transcription.";
            }
            return this.isRecording ? "Stop Recording" : "Start Recording";
        },
    },
    created() {
        this.loadHistory();
    },
    methods: {
        loadHistory() {
            const savedState = localStorage.getItem("ai-generation-state");
            if (savedState) {
                const state = JSON.parse(savedState);
                this.history = state.history || [];
                this.activePrompt = state.activePrompt || null;
            }
        },
        saveHistory() {
            const state = {
                history: this.history,
                activePrompt: this.activePrompt,
            };
            localStorage.setItem("ai-generation-state", JSON.stringify(state));
        },
        clearHistory() {
            this.history = [];
            this.activePrompt = null;
            localStorage.removeItem("ai-generation-state");
            this.stopRecording();
        },
        async toggleRecording() {
            if (this.isRecording) {
                await this.stopRecording();
            } else {
                await this.startRecording();
            }
        },
        async startRecording() {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                alert("Your browser does not support audio recording.");
                return;
            }

            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                this.mediaRecorder = new MediaRecorder(stream);
                this.audioChunks = [];

                this.mediaRecorder.ondataavailable = event => {
                    this.audioChunks.push(event.data);
                };

                this.mediaRecorder.onstop = async () => {
                    const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
                    stream.getTracks().forEach(track => track.stop());
                    await this.transcribeAudio(audioBlob);
                };

                this.mediaRecorder.start();
                this.isRecording = true;
            } catch (error) {
                console.error("Error starting recording:", error);
                alert("Could not start audio recording. Please check microphone permissions.");
            }
        },
        async stopRecording() {
            if (this.mediaRecorder && this.isRecording) {
                this.mediaRecorder.stop();
                this.isRecording = false;
            }
        },
        async transcribeAudio(audioBlob) {
            this.isTranscribing = true;
            try {
                const transcription = await TranscriptionService.transcribe(audioBlob, this.openaiApiKey, this.transcriptionLanguage);
                this.userInput = transcription;
            } catch (error) {
                console.error("Error transcribing audio:", error);
                alert("Could not transcribe audio. Please try again.");
            } finally {
                this.isTranscribing = false;
            }
        },
        async sendMessage() {
            if (!this.userInput.trim() || this.isLoading || !this.activePrompt) return;

            if (window.sa_event) {
                window.sa_event("ai_send_message");
            }

            const userMessage = { sender: "user", text: this.userInput };
            this.history.push(userMessage);
            const currentInput = this.userInput;
            this.userInput = "";
            this.isLoading = true;

            try {
                const response = await AIGenerationService.generate(this.resumeInfo, this.history, this.generationApiKey, this.generationModel, this.activePrompt);
                const assistantMessage = {
                    sender: "assistant",
                    text: response.answer,
                    resumeInfo: response.resumeInfo,
                };
                this.history.push(assistantMessage);
                this.$emit("update:resumeInfo", response.resumeInfo);
            } catch (error) {
                console.error("Error generating AI response:", error);
                const errorMessage = {
                    sender: "assistant",
                    text: "Sorry, I encountered an error. Please try again.",
                };
                this.history.push(errorMessage);
            } finally {
                this.isLoading = false;
                this.saveHistory();
            }
        },
        restoreCheckpoint(index) {
            const checkpoint = this.history[index];
            if (checkpoint && checkpoint.resumeInfo) {
                this.$emit("update:resumeInfo", checkpoint.resumeInfo);
                this.history = this.history.slice(0, index + 1);
                this.saveHistory();
            }
        },
        formatMessage(text) {
            return text.replace(/\n/g, '<br>');
        },
        async startConversation(prompt, resumeInfo) {
            if (this.isLoading) return;
            this.isLoading = true;
            this.activePrompt = prompt;

            try {
                const response = await AIGenerationService.generate(resumeInfo, [], this.generationApiKey, this.generationModel, prompt);
                const assistantMessage = {
                    sender: "assistant",
                    text: response.answer,
                    resumeInfo: response.resumeInfo,
                };
                this.history.push(assistantMessage);
                this.$emit("update:resumeInfo", response.resumeInfo);
            } catch (error) {
                console.error("Error generating AI response:", error);
                const errorMessage = {
                    sender: "assistant",
                    text: `Sorry, I encountered an error: ${error.message}`,
                };
                this.history.push(errorMessage);
            } finally {
                this.isLoading = false;
                this.saveHistory();
            }
        },
        hasSettings() {
            if (this.generationApiKey && this.generationModel) return true;
            this.$emit("need-settings");
            return false;
        },
        startFromScratch() {
            if (!this.hasSettings()) return;
            if (window.sa_event) {
                window.sa_event("ai_start_from_scratch");
            }
            const newResume = { basics: {} };
            this.$emit("update:resumeInfo", newResume);
            this.startConversation(PROMPT_SCRATCH, newResume);
        },
        editExistingResume() {
            if (!this.hasSettings()) return;
            if (window.sa_event) {
                window.sa_event("ai_edit_existing_resume");
            }
            this.startConversation(PROMPT_EDIT, this.resumeInfo);
        },
        handleEnter(event) {
            if (!event.shiftKey) {
                event.preventDefault();
                this.sendMessage();
            }
        },
    },
    unmounted() {
        this.stopRecording();
    },
};
</script>

<style scoped src="./AIGeneration.css"></style>